import { ThresholdsListFiltersDto } from '@dto/threshold/thresholds-list-filters.dto';
import { ApiCall } from '@dto/api-calls.dto';
import { createReducer, on } from '@ngrx/store';
import { ThresholdByProductId } from '@dto/threshold/threshold.dto';
import {
  initFilters,
  loadThresholds,
  loadThresholdsSuccess,
  loadThresholdsFailure,
  changeThresholdsFilters,
  changeThresholdsFiltersSuccess,
  changeThresholdsFiltersFailure,
  upsertNewThresholdsSuccess,
  deleteThresholdsSuccess,
  updateThresholdPrioritySuccess
} from '@store/thresholds-list/thresholds-list.action';

export const defaultThresholdsListFilter: ThresholdsListFiltersDto = {
  productId: [],
  eventType: [],
  indicator: [],
};

export interface ThresholdsListState {
  filters: {
    allData: Record<string, never>;
    thresholdsListFilter: ThresholdsListFiltersDto;
  };
  thresholds: ApiCall<ThresholdByProductId>;
}

export const initialState: ThresholdsListState = {
  filters: {
    allData: {},
    thresholdsListFilter: defaultThresholdsListFilter,
  },
  thresholds: { data: {}, isLoading: false, error: null },
};

export const thresholdsListReducer = createReducer(
  initialState,
  
  // Init filters
  on(initFilters, (state, { filters }) => ({
    ...state,
    filters: {
      ...state.filters,
      thresholdsListFilter: filters,
    },
  })),

  // Load thresholds
  on(loadThresholds, (state) => ({
    ...state,
    thresholds: {
      ...state.thresholds,
      isLoading: true,
      error: null,
    },
  })),
  
  on(loadThresholdsSuccess, (state, { thresholds }) => ({
    ...state,
    thresholds: {
      data: {
        ...state.thresholds.data,
        ...thresholds
      },
      isLoading: false,
      error: null,
    },
  })),
  
  on(loadThresholdsFailure, (state, { error }) => ({
    ...state,
    thresholds: {
      ...state.thresholds,
      isLoading: false,
      error,
    },
  })),
  
  // Thresholds list filters
  on(changeThresholdsFilters, (state) => ({
    ...state,
    filters: {
      ...state.filters,
      thresholdsListFilter: {
        ...state.filters.thresholdsListFilter,
        isLoading: true,
      },
    },
  })),

  on(changeThresholdsFiltersSuccess, (state, { filters }) => ({
    ...state,
    filters: {
      ...state.filters,
      thresholdsListFilter: { ...filters },
    },
  })),
  
  on(
    changeThresholdsFiltersFailure,
    (state, { filters }) => ({
      ...state,
      filters: {
        ...state.filters,
        thresholdsListFilter: { ...filters },
      },
    })
  ),

  // Upsert threshold (create or update)
  // Note: State will be updated by reloading thresholds from backend to get lastModifiedBy
  on(upsertNewThresholdsSuccess, (state) => state),

  // Delete threshold
  on(deleteThresholdsSuccess, (state, { productId, eventType, indicatorName }) => {
    const updatedThresholds = { ...state.thresholds.data };

    // Check if productId and eventType exist
    if (updatedThresholds[productId]?.[eventType]?.thresholds?.[indicatorName]) {
      // Clone the nested structure to maintain immutability
      updatedThresholds[productId] = {
        ...updatedThresholds[productId],
        [eventType]: {
          ...updatedThresholds[productId][eventType],
          thresholds: { ...updatedThresholds[productId][eventType].thresholds }
        }
      };

      // Delete the specific threshold
      delete updatedThresholds[productId][eventType].thresholds[indicatorName];

      // If no more thresholds for this eventType, remove the eventType
      if (Object.keys(updatedThresholds[productId][eventType].thresholds).length === 0) {
        const { [eventType]: _, ...remainingEventTypes } = updatedThresholds[productId];
        updatedThresholds[productId] = remainingEventTypes;

        // If no more eventTypes for this productId, remove the productId
        if (Object.keys(updatedThresholds[productId]).length === 0) {
          const { [productId]: __, ...remainingProducts } = updatedThresholds;
          return {
            ...state,
            thresholds: {
              ...state.thresholds,
              data: remainingProducts
            }
          };
        }
      }
    }

    return {
      ...state,
      thresholds: {
        ...state.thresholds,
        data: updatedThresholds
      }
    };
  }),

  // Update threshold priority
  on(updateThresholdPrioritySuccess, (state, { productId, eventType, priority }) => {
    const updatedThresholds = { ...state.thresholds.data };

    // Check if productId and eventType exist
    if (updatedThresholds[productId]?.[eventType]) {
      // Clone the nested structure to maintain immutability
      updatedThresholds[productId] = {
        ...updatedThresholds[productId],
        [eventType]: {
          ...updatedThresholds[productId][eventType],
          priority: priority
        }
      };
    }

    return {
      ...state,
      thresholds: {
        ...state.thresholds,
        data: updatedThresholds
      }
    };
  })
);